import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Subject, Subscription, fromEvent, interval } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { DataServiceService } from './data-service.service';
import { AuthService } from './auth.service';

/**
 * Contador de leads del sitio disponibles para el menú del vendedor (SL-05:
 * en lugar de la campana). Mismo patrón que MessagingService: sondea cada
 * 2 minutos solo con la pestaña visible.
 */
@Injectable({ providedIn: 'root' })
export class SiteLeadsCounterService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private pollSub?: Subscription;

  readonly availableCount$ = new BehaviorSubject<number>(0);

  private readonly POLL_MS = 120_000;

  constructor(
    private dataService: DataServiceService,
    private authService: AuthService,
  ) {}

  // Vendedores (SL-08) y admins (ven cuántos leads siguen sin tomar).
  private canSee(): boolean {
    return this.authService.isAffiliate() || this.authService.isAdmin();
  }

  /** Call once from AuthLayoutComponent.ngOnInit. */
  start(): void {
    if (this.pollSub || !this.canSee()) return;

    this.pollSub = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => document.visibilityState === 'visible'),
      distinctUntilChanged(),
      switchMap(visible => visible ? interval(this.POLL_MS).pipe(startWith(0)) : EMPTY),
      takeUntil(this.destroy$),
      // catchError adentro: un error (red, 401) no debe matar el sondeo.
      switchMap(() => this.dataService.getSiteLeadsAvailableCount().pipe(catchError(() => EMPTY))),
    ).subscribe(count => this.availableCount$.next(count));
  }

  /** Refresh right away (after claiming or releasing a lead). */
  refresh(): void {
    if (!this.canSee()) return;
    this.dataService.getSiteLeadsAvailableCount().subscribe({
      next: count => this.availableCount$.next(count),
      error: () => {},
    });
  }

  set(count: number): void {
    this.availableCount$.next(count);
  }

  stop(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
    this.availableCount$.next(0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
